/**
 * ID Type Safety
 *
 * Simple branded type for UUIDs to prevent passing invalid IDs (like "undefined" strings)
 * through the app. Validate once at boundaries, use safely everywhere else.
 */

/**
 * A validated UUID string. If you have this type, it's guaranteed to be a real UUID,
 * not "undefined", "null", or empty string.
 *
 * This is a "branded type" - TypeScript enforces you can't use a regular string
 * where UUID is expected. You must validate first using isUUID().
 */
declare const UUIDBrand: unique symbol;
export type UUID = string & { readonly [UUIDBrand]: typeof UUIDBrand };

/**
 * Check if a value is a valid UUID.
 *
 * Use this at system boundaries (route params, API responses) to validate
 * and narrow the type from string to UUID.
 *
 * @param value - The value to check
 * @returns True if value is a valid UUID (narrows type to UUID)
 *
 * @example
 * ```typescript
 * const routeParam = route.params.moduleId; // string
 *
 * if (isUUID(routeParam)) {
 *   // TypeScript now knows routeParam is UUID
 *   setSelectedModule({ moduleId: routeParam, courseId });
 * }
 * ```
 */
export function isUUID(value: unknown): value is UUID {
  if (typeof value !== 'string') return false;
  if (!value || value.trim() === '') return false;
  if (value === 'undefined' || value === 'null' || value === 'None') return false;

  // Strict UUID format validation (v4/v5 compatible)
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(value);
}

/**
 * Check if all provided values are valid UUIDs.
 * Convenience function for validating multiple IDs at once.
 */
export function areAllUUIDs(...values: unknown[]): boolean {
  return values.every(isUUID);
}
